
import Layout from "@/components/layout/Layout";
import { OptimizedImage } from "@/components/ui/image";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Award, 
  Target, 
  Code, 
  Users, 
  LineChart, 
  Workflow, 
  Clock, 
  ChevronRight,
  Lightbulb,
  GraduationCap,
  Briefcase,
  Link as LinkIcon,
  ArrowRight,
  FolderKanban
} from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <Layout title="About Me | Freelance With Andy" path="/about">
      <div className="relative bg-gradient-to-b from-slate-50 to-white pb-20">
        {/* Hero section with decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl opacity-50"></div>
          <div className="absolute top-60 -left-20 w-60 h-60 rounded-full bg-primary/5 blur-3xl opacity-30"></div>
        </div>
        
        {/* Main content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              <span className="text-[#34BF49]">Technical Product Manager</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bridging business strategy and development excellence
            </p>
          </div>
          
          {/* Main grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Left sidebar with profile and stats */}
            <div className="md:col-span-4">
              <div className="sticky top-24">
                {/* Profile image with styled container */}
                <div className="relative mb-8 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl rounded-2xl">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-primary/20 to-primary/20 transform rotate-6"></div>
                  <div className="relative aspect-square overflow-hidden rounded-xl border border-gray-200 shadow-md bg-white p-2">
                    <OptimizedImage
                      src="/lovable-uploads/ba4f0776-af2f-4f44-a112-8b601bcf43b8.png" 
                      alt="Andy's Profile" 
                      className="w-full h-full object-cover rounded-lg"
                      width={300}
                      height={300}
                    />
                  </div>
                </div>
                
                {/* Professional highlights card */}
                <Card className="mb-6 overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60"></div>
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <Award className="mr-2 h-5 w-5 text-primary" /> Professional Highlights
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex gap-3 items-start group">
                        <span className="flex-shrink-0 mt-1 p-1.5 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <Clock className="h-4 w-4 text-primary" />
                        </span>
                        <div>
                          <span className="block font-medium text-gray-900">Experience</span>
                          <span className="text-gray-600">10+ Years of product leadership</span>
                        </div>
                      </li>
                      <li className="flex gap-3 items-start group">
                        <span className="flex-shrink-0 mt-1 p-1.5 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <LineChart className="h-4 w-4 text-primary" />
                        </span>
                        <div>
                          <span className="block font-medium text-gray-900">Delivered</span>
                          <span className="text-gray-600">25+ Successful Products</span>
                        </div>
                      </li>
                      <li className="flex gap-3 items-start group">
                        <span className="flex-shrink-0 mt-1 p-1.5 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <Code className="h-4 w-4 text-primary" />
                        </span>
                        <div>
                          <span className="block font-medium text-gray-900">Tech Stack</span>
                          <span className="text-gray-600">Modern JavaScript, React, Node.js</span>
                        </div>
                      </li>
                      <li className="flex gap-3 items-start group">
                        <span className="flex-shrink-0 mt-1 p-1.5 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <Users className="h-4 w-4 text-primary" />
                        </span>
                        <div>
                          <span className="block font-medium text-gray-900">Team Leadership</span>
                          <span className="text-gray-600">Up to 15 engineers</span>
                        </div>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
                
                {/* Core competencies card */}
                <Card className="mb-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="h-1.5 bg-gradient-to-r from-primary/60 to-primary"></div>
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <Target className="mr-2 h-5 w-5 text-primary" /> Core Competencies
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Product Strategy</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Agile/Scrum</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">User Research</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Market Analysis</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Roadmapping</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Backlog Management</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Technical Documentation</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">UX/UI Design</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Data Analytics</Badge>
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-medium px-3 py-1 hover:bg-primary/5 hover:border-primary/20 transition-colors cursor-default">Stakeholder Management</Badge>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Projects Link Card (NEW) */}
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60"></div>
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <FolderKanban className="mr-2 h-5 w-5 text-primary" /> My Projects
                    </h3>
                    <p className="text-gray-600 mb-4">
                      View my portfolio of completed projects and case studies showcasing my technical and product management skills.
                    </p>
                    <Link to="/projects">
                      <Button className="w-full bg-primary hover:bg-primary/90 text-white">
                        View Projects <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Main content area */}
            <div className="md:col-span-8">
              <div className="prose max-w-none">
                {/* Quote section */}
                <div className="mb-10 p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:translate-y-[-2px]">
                  <div className="flex">
                    <div className="text-5xl text-primary opacity-20 leading-none">"</div>
                    <p className="text-xl italic text-gray-700 pl-2">
                      I transform complex business problems into elegant technical solutions, creating products that delight users while driving measurable business outcomes.
                    </p>
                  </div>
                </div>
                
                {/* Approach section */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                    <Workflow className="mr-3 h-6 w-6 text-primary" /> 
                    <span className="relative">
                      My Approach to Product Management
                      <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-primary to-transparent"></span>
                    </span>
                  </h2>
                  
                  <p className="mb-6 text-gray-600">
                    With over a decade of experience in technical product management, I've developed a methodology that consistently delivers successful digital products. I combine strategic business thinking with deep technical knowledge to lead cross-functional teams toward exceptional outcomes.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <Card className="border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300 transform hover:translate-y-[-2px] overflow-hidden group">
                      <div className="h-1 w-full bg-primary/30 transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300"></div>
                      <CardContent className="pt-6">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                          <Lightbulb className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-gray-800">Discovery & Strategy</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Deep stakeholder interviews to identify true business goals</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Data-driven market analysis to validate opportunities</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Collaborative roadmap development with clear success metrics</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                    
                    <Card className="border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300 transform hover:translate-y-[-2px] overflow-hidden group">
                      <div className="h-1 w-full bg-primary/30 transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300"></div>
                      <CardContent className="pt-6">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                          <Briefcase className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-gray-800">Execution & Delivery</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Technical specification writing that bridges business and development</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Rigorous prioritization frameworks for maximum impact</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Agile team leadership with iterative feedback loops</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </section>
                
                {/* Philosophy section */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                    <GraduationCap className="mr-3 h-6 w-6 text-primary" /> 
                    <span className="relative">
                      Technical Product Management Philosophy
                      <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-primary to-transparent"></span>
                    </span>
                  </h2>
                  
                  <p className="mb-4 text-gray-600">
                    As a technical product manager, I believe in diving deep into the codebase rather than remaining at a surface level. My engineering background enables me to:
                  </p>
                  
                  <ul className="space-y-4 mb-6 pl-2">
                    <li className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="mr-3 mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-600">Collaborate effectively with development teams, understanding technical constraints and opportunities</span>
                    </li>
                    <li className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="mr-3 mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-600">Make informed architectural decisions that balance short-term delivery with long-term scalability</span>
                    </li>
                    <li className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="mr-3 mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-600">Translate complex technical concepts for non-technical stakeholders</span>
                    </li>
                    <li className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="mr-3 mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-600">Identify technical debt before it becomes problematic</span>
                    </li>
                    <li className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="mr-3 mt-0.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-600">Bridge the gap between business objectives and technical implementation</span>
                    </li>
                  </ul>
                </section>
                
                {/* Toolkit section */}
                <section className="mb-10">
                  <div className="rounded-2xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                    <div className="h-1.5 bg-gradient-to-r from-primary to-primary"></div>
                    <div className="p-8">
                      <h3 className="text-xl font-bold mb-4 text-gray-800">My Product Management Toolkit</h3>
                      <p className="mb-6 text-gray-600">
                        I leverage a variety of tools and frameworks to deliver exceptional product outcomes:
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Discovery</span>
                          <p className="text-sm text-gray-600">Jobs-to-be-Done, User Interviews</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Planning</span>
                          <p className="text-sm text-gray-600">OKRs, Story Mapping</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Development</span>
                          <p className="text-sm text-gray-600">Agile/Scrum, Kanban</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Analytics</span>
                          <p className="text-sm text-gray-600">GA4, Mixpanel, Amplitude</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Technical</span>
                          <p className="text-sm text-gray-600">JIRA, GitHub, Figma</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                          <span className="font-bold text-gray-800 block mb-1">Collaboration</span>
                          <p className="text-sm text-gray-600">Notion, Slack, Miro</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                
                {/* CTA section */}
                <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-gray-50 to-white border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300">
                  <p className="text-xl font-medium mb-6 text-gray-700">
                    Ready to elevate your product strategy with technical expertise?
                  </p>
                  <Link to="/contact">
                    <Button className="bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all" size="lg">
                      Get your free call <LinkIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default About;
